// Rug Room Shots: "Approve AI-passed images" (SPEC_AICHECK 4.11 / 5.7). A person approves, in their own name, the
// images the AI check passed at or above a score they pick (never below the Pass score). Only images still waiting
// for review are approved: a person's decision is never overwritten. The AI itself never approves anything.
// 1. POST jobs/{id}/approve/preview (read-only) on open and, 400 ms after the last change, on every change: the count,
//    5-point score bands, every skipped row by reason, and a sample strip of the lowest-scoring images.
// 2. POST jobs/{id}/approve commits exactly preview.genIds with expectedCount (re-checked inside the backend's
//    transaction: 409 selection_changed carries a fresh preview). clientRef is made once per open: a repeat is refused
//    with 409 already_submitted and the first result, which counts as success. No money, so no daily limit.
// The parent mounts this dialog only while it is open, so every open starts fresh with a new clientRef.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, CircularProgress, Stack, Typography,
  FormControlLabel, RadioGroup, Radio, Chip, Box, TextField, Tooltip,
} from '@mui/material';
import { MdDoneAll } from 'react-icons/md';
import rrsApi from './api';
import {
  fmtInt, errText, serverText, conflictCode, Tile, ImageLightbox, styleName, shapeName,
} from './common';

const makeClientRef = () => `ba-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10).padEnd(8, '0')}`;

const TIMEOUT_TEXT = 'The server did not answer in time. It may still have approved them. The list has been refreshed. '
  + 'Confirming again is safe: a repeat is refused if the first one went through.';

const SKIP_ORDER = ['not_found', 'other_job', 'not_succeeded', 'not_latest', 'not_pending', 'no_ai_check', 'ai_checking', 'ai_not_pass', 'ai_below'];
const skipText = (k, n, minScore) => {
  switch (k) {
    case 'not_found': return `${n} no longer exist`;
    case 'other_job': return `${n} belong to another job`;
    case 'not_succeeded': return `${n} have no finished image`;
    case 'not_latest': return `${n} have a newer attempt`;
    case 'not_pending': return `${n} already decided by a person`;
    case 'no_ai_check': return `${n} not checked by AI`;
    case 'ai_checking': return `${n} being checked again`;
    case 'ai_not_pass': return `${n} not an AI Pass`;
    case 'ai_below': return `${n} below ${minScore}`;
    default: return `${n} ${k.replace(/_/g, ' ')}`;
  }
};

const BulkApproveDialog = ({
  open, onClose, jobId, filter, ticked, config, notify, onDone, onRefresh, aiThresholds,
}) => {
  const passMin = Number(aiThresholds && aiThresholds.passMin) || Number(config && config.aiCheck && config.aiCheck.passMin) || 85;
  const hasTicked = Array.isArray(ticked) && ticked.length > 0;
  const [minText, setMinText] = useState(() => String(Math.max(passMin, 90)));
  const [mode, setMode] = useState(hasTicked ? 'selected' : 'view'); // view | selected
  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [changedMsg, setChangedMsg] = useState('');
  const [timeoutMsg, setTimeoutMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [clientRef] = useState(makeClientRef);
  const seq = useRef(0);
  const firstLoad = useRef(true);

  const minScore = /^\d{1,3}$/.test(minText.trim()) ? Number(minText.trim()) : NaN;
  const minOk = Number.isInteger(minScore) && minScore >= passMin && minScore <= 100;
  const style = (filter && filter.style) || '';
  const shape = (filter && filter.shape) || '';

  const request = useMemo(() => {
    if (!minOk) return null;
    const selection = mode === 'selected' && hasTicked
      ? { mode: 'ids', genIds: ticked }
      : { mode: 'filter', filter: { state: 'pending', style, shape, ai: 'pass', aiAtLeast: minScore } };
    return { selection, minScore };
  }, [minOk, mode, hasTicked, ticked, style, shape, minScore]);
  const requestKey = useMemo(() => (request ? JSON.stringify(request) : ''), [request]);

  const loadPreview = useCallback(async () => {
    if (!jobId || !request) return;
    const my = seq.current + 1;
    seq.current = my;
    const usedKey = requestKey;
    setLoading(true);
    setError('');
    try {
      const res = await rrsApi.approvePreview(jobId, request);
      if (my !== seq.current) return;
      setPreview(res);
      setPreviewKey(usedKey);
    } catch (e) {
      if (my === seq.current) setError(serverText(e));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [jobId, request, requestKey]);

  // Preview on open at once, then 400 ms after the last change (typing a score).
  useEffect(() => {
    if (!open || !request) return undefined;
    if (firstLoad.current) {
      firstLoad.current = false;
      loadPreview();
      return undefined;
    }
    const t = setTimeout(() => { loadPreview(); }, 400);
    return () => clearTimeout(t);
  }, [open, request, loadPreview]);

  const count = preview ? Number(preview.count) || 0 : 0;
  const requested = preview ? Number(preview.requested) || 0 : 0;
  const skipped = (preview && preview.skippedCounts) || {};
  const bands = preview && Array.isArray(preview.scoreBands) ? preview.scoreBands.filter((b) => Number(b.count) > 0) : [];
  const sample = preview && Array.isArray(preview.sample) ? preview.sample.slice(0, 12) : [];
  const fresh = !!preview && !!request && previewKey === requestKey;
  const nothing = !!preview && fresh && !loading && count === 0;
  const canConfirm = !!preview && fresh && !loading && !busy && count > 0;
  const shownMin = preview && preview.minScore ? preview.minScore : minScore;

  const finish = (res) => {
    const r = res || {};
    const n = r.approved !== undefined && r.approved !== null ? r.approved : r.count;
    notify(`Approved ${fmtInt(n)} image${Number(n) === 1 ? '' : 's'}.`, 'success');
    if (onDone) onDone(r);
  };

  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setChangedMsg('');
    setError('');
    setTimeoutMsg('');
    try {
      const res = await rrsApi.approveMany(jobId, {
        clientRef,
        genIds: preview.genIds || [],
        expectedCount: preview.count,
        minScore: request.minScore,
        selection: request.selection,
      });
      finish(res);
    } catch (e) {
      const code = conflictCode(e);
      if (code === 'already_submitted') {
        finish((e.body && e.body.current) || {});
      } else if (code === 'selection_changed' && e.body && e.body.current) {
        const cur = e.body.current;
        setPreview(cur);
        setPreviewKey(requestKey);
        setChangedMsg(`The selection changed: now ${fmtInt(cur.count)} images. Check and confirm again.`);
        if (onRefresh) onRefresh();
      } else if (!e || !e.status || e.status >= 500) {
        setTimeoutMsg(TIMEOUT_TEXT);
        if (onRefresh) await onRefresh();
        loadPreview();
      } else if (code) {
        // nothing_eligible and other refusals: the server's words, then a fresh preview.
        setError(serverText(e));
        loadPreview();
      } else {
        setError(e && e.status === 400 ? serverText(e) : errText(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const viewWords = [
    style ? styleName(style, config && config.styles) : 'every style',
    shape ? shapeName(shape, config && config.shapes) : 'every shape',
  ].join(', ');

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Approve AI-passed images
        <Typography variant="body2" color="text.secondary">
          Job #{jobId}. Only images waiting for review with an AI Pass are approved.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {changedMsg && <Alert severity="warning" sx={{ mb: 1.5 }}>{changedMsg}</Alert>}
        {timeoutMsg && <Alert severity="warning" sx={{ mb: 1.5 }}>{timeoutMsg}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <TextField
            size="small" type="number" label="Lowest AI score to approve" value={minText}
            onChange={(e) => setMinText(e.target.value.slice(0, 3))}
            inputProps={{ min: passMin, max: 100, step: 1 }}
            error={!minOk}
            helperText={minOk ? `From ${passMin} (the Pass score) to 100.` : `A whole number from ${passMin} to 100.`}
            sx={{ width: 240, flex: '0 0 auto' }}
            disabled={busy}
          />
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel
              value="view" control={<Radio size="small" />} disabled={busy}
              label={<Typography variant="body2">Images waiting for review in this view ({viewWords})</Typography>}
            />
            {hasTicked && (
              <FormControlLabel
                value="selected" control={<Radio size="small" />} disabled={busy}
                label={<Typography variant="body2">The {fmtInt(ticked.length)} selected images</Typography>}
              />
            )}
          </RadioGroup>
        </Stack>

        <Box sx={{ mt: 2 }}>
          {loading && !preview && <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
          {preview && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {fmtInt(count)} image{count === 1 ? '' : 's'} will be approved
                </Typography>
                {bands.map((b) => (
                  <Chip
                    key={`${b.from}-${b.to}`} size="small" color="success" variant="outlined"
                    label={`AI ${b.from}-${b.to}: ${fmtInt(b.count)}`}
                  />
                ))}
                {loading && <CircularProgress size={16} />}
              </Stack>
              {requested - count > 0 && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {fmtInt(requested - count)} {mode === 'selected' ? 'of the selected images ' : ''}will be left out:
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {SKIP_ORDER.filter((k) => Number(skipped[k]) > 0).map((k) => (
                      <li key={k}><Typography variant="body2">{skipText(k, fmtInt(skipped[k]), shownMin)}</Typography></li>
                    ))}
                  </Box>
                </Alert>
              )}
              {nothing && (
                <Typography color="text.secondary" sx={{ mt: 1.5 }}>
                  No image waiting for review has an AI Pass at {shownMin} or above here.
                </Typography>
              )}
              {sample.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                    The lowest scores of this set (click one to see it large next to the product photo; left / right
                    arrow keys flip between them):
                  </Typography>
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {sample.map((s) => (
                      <Tooltip key={s.genId} title={`${s.itemId || `gen ${s.genId}`} · AI ${s.score}`}>
                        <Box sx={{ position: 'relative' }}>
                          <Tile
                            src={s.thumbUrl} alt={s.itemId || ''} size={88} contain={false}
                            onClick={() => setLightbox(s)}
                          >
                            <Chip
                              size="small" color="success" label={s.score}
                              sx={{ position: 'absolute', bottom: 4, right: 4, height: 18, fontSize: '0.65rem', pointerEvents: 'none' }}
                            />
                          </Tile>
                        </Box>
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Box>
        <Alert severity="info" icon={false} sx={{ mt: 2 }}>
          The AI does not approve anything by itself. This approves these images in your name; you can still clear or
          change any decision.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{nothing ? 'Close' : 'Cancel'}</Button>
        {!nothing && (
          <Button variant="contained" color="success" startIcon={busy ? null : <MdDoneAll />} onClick={submit} disabled={!canConfirm}>
            {busy ? <CircularProgress size={20} color="inherit" /> : `Approve ${fmtInt(count)} image${count === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>

      {/* Review fix RP-3: the 1200 px result and the product photo (silo), so the design can be compared before
          approving many images (an older backend sends only the 300 px thumbnail: then that is shown). */}
      <ImageLightbox
        open={!!lightbox} onClose={() => setLightbox(null)}
        items={lightbox ? [
          { key: 'result', label: 'Result', src: lightbox.previewUrl || lightbox.thumbUrl },
          lightbox.silo ? {
            key: 'silo', label: 'Product photo', src: lightbox.silo.largeUrl || lightbox.silo.originalUrl,
            downloadUrl: lightbox.silo.originalUrl, downloadLabel: 'Open original',
          } : null,
        ] : []}
        initialKey="result"
        title={lightbox ? `${lightbox.itemId || `gen ${lightbox.genId}`} · AI ${lightbox.score}` : ''}
      />
    </Dialog>
  );
};

export default BulkApproveDialog;
